import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { TurnkeyProvider, useTurnkey, type TurnkeyProviderConfig } from "@turnkey/react-wallet-kit";
import { OtpType } from "@turnkey/core";
import { notEmpty } from "@orb-labs/types";
import {
  MeshProvider as MeshProviderRaw,
  PlatformStorageProvider,
  AuthenticationProvider as SharedAuthProviderRaw,
  MnemonicProvider as MnemonicProviderRaw,
  useMesh,
} from "@orb-labs/orby-react";
import type { PlatformStorage, MeshRuntime, AuthRuntime } from "@orb-labs/orby-react";
import { DEFAULT_CREATE_SUBORG_PARAMS } from "@orb-labs/orby-react/bullrun";
import { ENV } from "@/config/env";
import * as SecureStorage from "@/security/SecureStorage";
import { createWebPlatformStorage, type EncryptionBackend } from "@/security/SecureStorage";
import { ensureStorageKey, clearStorageKeyId } from "@/security/TurnkeyKeyManager";
import { useWebMeshRuntime } from "@/hooks/useWebMeshRuntime";

// Shared providers are typed against @types/react@18 which lacks bigint in ReactNode.
const MeshProviderBridge = MeshProviderRaw as unknown as React.FC<{
  storage: PlatformStorage;
  meshRuntime?: MeshRuntime;
  children: React.ReactNode;
}>;
const MnemonicProvider = MnemonicProviderRaw as unknown as React.FC<{
  checkSecurity: () => void;
  children: React.ReactNode;
}>;

// Web has no device-security gate (no jailbreak/hook detection). The shared
// MnemonicProvider requires a checkSecurity callback so mobile can inject its
// DeviceSecurity check — web passes a noop.
const noopSecurityCheck = (): void => undefined;
const SharedAuthenticationProvider = SharedAuthProviderRaw as unknown as React.FC<{
  authRuntime: AuthRuntime;
  children: React.ReactNode;
}>;

/**
 * Re-loads encrypted Mesh tokens once Turnkey storage-key derivation finishes.
 * Previously the parent remounted the whole provider tree (`key="pending"` →
 * `"ready"`), which wiped discover rails after their first fetch. Storage reads
 * go through a ref, so Mesh can stay mounted and hydrate when encryption lands.
 */
function MeshEncryptionReadyHydrator({ encryptionReady }: { encryptionReady: boolean }) {
  const { refreshLinkedAccounts, refreshMeshHoldings } = useMesh();
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!encryptionReady) {
      hydratedRef.current = false;
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    refreshLinkedAccounts()
      .then(() => refreshMeshHoldings())
      .catch((err) => {
        console.error("[MeshEncryptionReadyHydrator] Failed to hydrate mesh state:", err);
      });
  }, [encryptionReady, refreshLinkedAccounts, refreshMeshHoldings]);

  return null;
}

/**
 * Bridge that creates the PlatformStorage + Mesh + Mnemonic + Auth infrastructure
 * inside TurnkeyProvider context (where wallet signers are available).
 */
function InfrastructureBridge({ children }: { children: React.ReactNode }) {
  const {
    clientState,
    user,
    session,
    wallets,
    httpClient,
    logout: turnkeyLogout,
    fetchWalletProviders,
    disconnectWalletAccount,
    signTransaction: turnkeySignTransaction,
    signMessage: turnkeySignMessage,
    initOtp: turnkeyInitOtpRaw,
    completeOtp: turnkeyCompleteOtpRaw,
    handleGoogleOauth: turnkeyHandleGoogleOauthRaw,
  } = useTurnkey();

  const userId = session?.userId;

  const encryptionBackendRef = useRef<EncryptionBackend | undefined>(undefined);
  const [encryptionReady, setEncryptionReady] = useState(false);

  useEffect(() => {
    if (clientState !== "ready" || !notEmpty(userId) || !notEmpty(httpClient)) {
      encryptionBackendRef.current = undefined;
      setEncryptionReady(false);
      return;
    }
    let cancelled = false;
    ensureStorageKey(httpClient, userId)
      .then((keyId) => {
        if (!cancelled) {
          encryptionBackendRef.current = { httpClient, privateKeyId: keyId };
          setEncryptionReady(true);
        }
      })
      .catch((err) => {
        console.error("[AuthenticationProvider] Failed to ensure storage key:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [clientState, userId, httpClient]);

  useEffect(() => {
    if (!session) {
      encryptionBackendRef.current = undefined;
      setEncryptionReady(false);
    }
  }, [session]);

  const storage = useMemo<PlatformStorage>(() => createWebPlatformStorage(encryptionBackendRef), []);

  const { runtime: meshRuntime, MeshHost } = useWebMeshRuntime();

  const additionalLogoutSteps = useCallback(async () => {
    try {
      const providers = await fetchWalletProviders();
      const connected = providers.filter((p) => p.connectedAddresses && p.connectedAddresses.length > 0);
      await Promise.allSettled(connected.map((p) => disconnectWalletAccount(p).catch(() => {})));
    } catch {
      // Best-effort: don't block logout if wallet disconnect fails
    }
    if (notEmpty(userId)) {
      clearStorageKeyId(userId);
    }
    SecureStorage.clearAll();
  }, [fetchWalletProviders, disconnectWalletAccount, userId]);

  const authRuntime: AuthRuntime = useMemo(
    () => ({
      clientState: clientState as string,
      session: notEmpty(userId) ? { userId } : undefined,
      user: user
        ? {
            userEmail: user.userEmail ?? undefined,
            userPhoneNumber: user.userPhoneNumber ?? undefined,
          }
        : undefined,
      wallets: wallets
        .filter((w) => notEmpty(w.accounts) && w.accounts.length > 0)
        .map((w) => ({
          // Pass through full Turnkey account objects so the SDK receives source, walletId, etc.
          accounts: w.accounts.map((a) => ({ ...a })),
        })),
      turnkeySignTransaction: async (params) =>
        turnkeySignTransaction({
          unsignedTransaction: params.unsignedTransaction,
          transactionType: params.transactionType as never,
          walletAccount: params.walletAccount as never,
        }),
      turnkeySignMessage: async (params) =>
        turnkeySignMessage({
          message: params.message,
          walletAccount: params.walletAccount as never,
          encoding: params.encoding as never,
          hashFunction: params.hashFunction as never,
          addEthereumPrefix: params.addEthereumPrefix,
        }),
      turnkeyLogout: async () => {
        await turnkeyLogout();
      },
      turnkeyInitOtp: async (params) =>
        turnkeyInitOtpRaw({
          contact: params.contact,
          otpType: params.otpType === "EMAIL" ? OtpType.Email : OtpType.Sms,
        }),
      turnkeyCompleteOtp: async (params) =>
        turnkeyCompleteOtpRaw({
          otpId: params.otpId,
          otpCode: params.otpCode,
          contact: params.contact,
          otpType: params.otpType === "EMAIL" ? OtpType.Email : OtpType.Sms,
        }),
      turnkeyHandleGoogleOauth: async () => {
        await turnkeyHandleGoogleOauthRaw();
      },
      // Apple OAuth is disabled in this app's TurnkeyProviderConfig
      // (appleOauthEnabled: false), so omit the runtime method — screens
      // reading loginWithApple via useAuthentication() will see undefined
      // and hide the button.
      additionalLogoutSteps,
    }),
    [
      clientState,
      userId,
      user,
      wallets,
      turnkeySignTransaction,
      turnkeySignMessage,
      turnkeyLogout,
      turnkeyInitOtpRaw,
      turnkeyCompleteOtpRaw,
      turnkeyHandleGoogleOauthRaw,
      additionalLogoutSteps,
    ]
  );

  return (
    <PlatformStorageProvider value={storage}>
      <MeshProviderBridge storage={storage} meshRuntime={meshRuntime}>
        <MeshHost />
        <MeshEncryptionReadyHydrator encryptionReady={encryptionReady} />
        <MnemonicProvider checkSecurity={noopSecurityCheck}>
          <SharedAuthenticationProvider authRuntime={authRuntime}>{children}</SharedAuthenticationProvider>
        </MnemonicProvider>
      </MeshProviderBridge>
    </PlatformStorageProvider>
  );
}

export function AuthenticationProvider({ children }: { children: React.ReactNode }) {
  const turnkeyConfig: TurnkeyProviderConfig = useMemo(
    () => ({
      organizationId: ENV.TURNKEY_ORG_ID,
      authProxyConfigId: ENV.TURNKEY_AUTH_PROXY_CONFIG_ID,
      auth: {
        methods: {
          emailOtpAuthEnabled: true,
          smsOtpAuthEnabled: true,
          walletAuthEnabled: false,
          googleOauthEnabled: true,
          appleOauthEnabled: false,
        },
        oauthConfig: {
          googleClientId: ENV.GOOGLE_CLIENT_ID || undefined,
          oauthRedirectUri: ENV.TURNKEY_OAUTH_REDIRECT_URI || undefined,
        },
        createSuborgParams: DEFAULT_CREATE_SUBORG_PARAMS,
        autoRefreshSession: true,
      },
      walletConfig: {
        features: {
          connecting: false,
        },
        chains: {
          ethereum: {
            native: true,
          },
          solana: {
            native: true,
          },
        },
      },
      ui: {
        darkMode: true,
        supressMissingStylesError: true,
      },
    }),
    []
  );

  return (
    <TurnkeyProvider
      config={turnkeyConfig}
      callbacks={{
        onError: (error) => {
          console.error("Turnkey error:", error);
        },
      }}
    >
      <InfrastructureBridge>{children}</InfrastructureBridge>
    </TurnkeyProvider>
  );
}
